/**
 * Utilitários para manipulação de contatos WhatsApp
 */

/**
 * Formata o JID do WhatsApp para exibição
 */
export const formatWhatsAppJid = (jid: string): string => {
  if (!jid) return 'N/A'
  
  // Remove sufixos do WhatsApp
  const cleanJid = jid.replace('@s.whatsapp.net', '').replace('@g.us', '')
  
  // Se for um número de telefone, formatar
  if (/^\d+$/.test(cleanJid)) {
    // Assumindo formato brasileiro: +55 (11) 99999-9999
    if (cleanJid.length >= 10) {
      const country = cleanJid.slice(0, 2)
      const area = cleanJid.slice(2, 4)
      const number = cleanJid.slice(4)
      
      if (number.length === 9) {
        return `+${country} (${area}) ${number.slice(0, 5)}-${number.slice(5)}`
      } else if (number.length === 8) {
        return `+${country} (${area}) ${number.slice(0, 4)}-${number.slice(4)}`
      }
    }
    
    return `+${cleanJid}`
  }
  
  return cleanJid
}

/**
 * Determina se o contato é um grupo
 */
export const isGroupContact = (jid: string): boolean => {
  return jid.endsWith('@g.us')
}

/**
 * Extrai o nome de exibição do contato
 */
export const getDisplayName = (contact: {
  name?: string | null
  display_name?: string | null
  push_name_last?: string | null
  jid: string
}): string => {
  if (contact.name) {
    return contact.name
  }

  if (contact.display_name) {
    return contact.display_name
  }

  if (contact.push_name_last) {
    return contact.push_name_last
  }

  // Fallback para o JID formatado
  return formatWhatsAppJid(contact.jid)
}

/**
 * Formata a data da última mensagem
 */
export const formatLastMessageDate = (lastMessageAt: string | null): string => {
  if (!lastMessageAt) {
    return 'Nunca'
  }
  
  const date = new Date(lastMessageAt)
  const now = new Date()
  const diffInMs = now.getTime() - date.getTime()
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))
  
  if (diffInDays === 0) {
    return 'Hoje'
  } else if (diffInDays === 1) {
    return 'Ontem'
  } else if (diffInDays < 7) {
    return `${diffInDays} dias atrás`
  } else {
    return date.toLocaleDateString('pt-BR')
  }
}

/**
 * Obtém o telefone formatado do contato
 * Para exportação: retorna "none" se não houver nome
 */
export const getContactPhone = (contact: {
  phone?: string | null
  jid: string
}): string => {
  if (contact.phone) {
    return contact.phone
  }

  return formatWhatsAppJid(contact.jid)
}

/**
 * Obtém o telefone formatado para exportação
 * Retorna "none" se o contato não tiver nome definido
 */
export const getContactPhoneForExport = (contact: {
  name?: string | null
  display_name?: string | null
  push_name_last?: string | null
  phone?: string | null
  jid: string
}): string => {
  // Verifica se o contato tem algum nome definido
  const hasName = contact.name || contact.display_name || contact.push_name_last

  // Se não tiver nome, retorna "none"
  if (!hasName) {
    return 'none'
  }

  // Se tiver nome, retorna o telefone normalmente
  if (contact.phone) {
    return contact.phone
  }

  return formatWhatsAppJid(contact.jid)
}

/**
 * Gera URL de avatar baseado em profile picture ou iniciais
 * Tenta usar a foto real se disponível, senão retorna SVG com iniciais
 */
export const getContactAvatarUrl = (contact: {
  name?: string | null
  display_name?: string | null
  push_name_last?: string | null
  jid: string
  profile_picture?: string | null
}): string => {
  // Se tiver profile_picture, usar ela
  if (contact.profile_picture) {
    return contact.profile_picture
  }

  // Senão, gerar SVG com iniciais
  return generateContactAvatar(contact)
}

/**
 * Gera avatar baseado no nome ou JID
 */
export const generateContactAvatar = (contact: {
  name?: string | null
  display_name?: string | null
  push_name_last?: string | null
  jid: string
}): string => {
  const name = getDisplayName(contact)
  const asciiLetterRegex = /[A-Za-z0-9\u00C0-\u024F]/
  const rawInitials = name
    .split(' ')
    .map(word => {
      for (const char of word) {
        if (asciiLetterRegex.test(char)) return char
      }
      return ''
    })
    .filter(Boolean)
    .join('')
    .toUpperCase()
    .slice(0, 2)

  const initials = rawInitials || (contact.jid ? contact.jid.replace(/\D/g, '').slice(-2) || '?' : '?')

  const backgroundColor = '#a1a1aa'
  const textColor = '#000000'

  const svg = `<svg width="40" height="40" xmlns="http://www.w3.org/2000/svg"><circle cx="20" cy="20" r="20" fill="${backgroundColor}"/><text x="20" y="26" text-anchor="middle" fill="${textColor}" font-family="Arial" font-size="14" font-weight="bold">${initials}</text></svg>`

  try {
    return `data:image/svg+xml,${encodeURIComponent(svg)}`
  } catch {
    const fallbackSvg = `<svg width="40" height="40" xmlns="http://www.w3.org/2000/svg"><circle cx="20" cy="20" r="20" fill="${backgroundColor}"/><text x="20" y="26" text-anchor="middle" fill="${textColor}" font-family="Arial" font-size="14" font-weight="bold">?</text></svg>`
    return `data:image/svg+xml,${encodeURIComponent(fallbackSvg)}`
  }
}

/**
 * Formata a data de cadastro do contato
 */
export const formatCreatedDate = (createdAt: string | null): string => {
  if (!createdAt) {
    return 'Data desconhecida'
  }

  const date = new Date(createdAt)
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

/**
 * Valida se uma string é um UUID válido
 */
export const isValidUUID = (uuid: string): boolean => {
  if (!uuid || typeof uuid !== 'string') {
    return false
  }

  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  return uuidRegex.test(uuid)
}

/**
 * Formata telefone internacional com código do país
 */
export const formatInternationalPhone = (phone: string, countryCode: string = '55'): string => {
  // Remove caracteres não numéricos
  const cleanPhone = phone.replace(/\D/g, '')

  // Se já começa com o código do país, retorna como está
  if (cleanPhone.startsWith(countryCode)) {
    return cleanPhone
  }

  // Adiciona o código do país
  return `${countryCode}${cleanPhone}`
}